import {
  coolGray60,
  cyan60,
  green60,
  magenta50,
  purple50,
  purple60,
  yellow30,
} from "@carbon/colors";

import { ThemeColors } from "src/theme/ThemeProvider";

import { elementCodes } from "../graph.utils";

export const getOldLayout = (algorithm: "concentric" | "elk") => ({
  name: algorithm, // concentric, breadthfirst, elk
  minNodeSpacing: 100,
  elk: {
    "spacing.nodeNode": 350, // Adjust node-to-node spacing
    "spacing.edgeEdge": 100, // Adjust edge-to-edge spacing
    "spacing.nodeSelfLoop": 100, // Adjust self-loop spacing
    "spacing.nodeNodeBetweenLayers": 250, // Spacing between nodes in different layers (layered layout)
    // algorithm: controls which particular layout algorithm is used.
    // https://cytoscape.org/cytoscape.js-elk/?demo=box
    algorithm: "layered",
    "elk.direction": "DOWN", // LEFT, RIGHT, UP
  },
  concentric: function (node) {
    // Define concentric levels based on node type
    if (node.data("type") === "element") {
      return 10; // Closest to the center
    } else if (node.data("type") === "identifier") {
      return 100; // Second level
    } else if (node.data("type") === "case") {
      return 1; // Outermost level
    }
    return 0;
  },
  padding: 20,
  directed: true,
  fit: true,
});

const nodeStyle = (colors: ThemeColors) => ({
  label: "data(label)",
  "font-family": "IBM Plex Sans",
  "font-size": 18,
  "font-weight": "500",
  color: colors.text.primary,
  "text-valign": "bottom",
  "text-halign": "center",
  "text-margin-y": 5,
  width: 70,
  height: 70,
  "background-color": colors.text.primary,
  "border-width": "1",
  "border-color": coolGray60,
  "background-width-relative-to": "inner",
  "background-height-relative-to": "inner",
  "background-width": "32px",
  "background-height": "32px",
});

// Stylesheet for nodes and edges
export const oldStyles = (colors: ThemeColors) => [
  // Styling for case nodes
  {
    selector: 'node[type="case"]',
    style: {
      ...nodeStyle(colors),
      shape: "circle",
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return cyan60;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(cyan60);
      },
      "background-width-relative-to": "inner",
      "background-height-relative-to": "inner",
      "background-width": "32px",
      "background-height": "32px",
    },
  },
  // Styling for the alert case
  {
    selector: 'node[type="case-alert"]',
    style: {
      ...nodeStyle(colors),
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return yellow30;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(yellow30);
      },
      "background-width-relative-to": "inner",
      "background-height-relative-to": "inner",
      "background-width": "32px",
      "background-height": "32px",
      shape: "circle",
    },
  },
  // Styling for element nodes
  {
    selector: 'node[type="element"]',
    style: {
      ...nodeStyle(colors),
      shape: "circle",
      "background-width-relative-to": "inner",
      "background-height-relative-to": "inner",
      "background-width": "32px",
      "background-height": "32px",
    },
  },
  // Styling for identifier nodes
  {
    selector: 'node[type="identifier"]',
    style: {
      ...nodeStyle(colors),
      shape: "rectangle",
      width: 40,
      height: 40,
      "background-width-relative-to": "inner",
      "background-height-relative-to": "inner",
      "background-width": "32px",
      "background-height": "32px",
    },
  },
  // Styling for edges
  {
    selector: 'edge[type="edge-case"]',
    style: {
      width: 2,
      "line-color": coolGray60,
      "target-arrow-color": coolGray60,
      "target-arrow-shape": "triangle",
      "curve-style": "bezier",
    },
  },
  {
    selector: 'edge[type="edge-elem"]',
    style: {
      width: 2,
      "line-color": purple50,
      "target-arrow-color": purple50,
      "target-arrow-shape": "triangle",
      "curve-style": "bezier",
    },
  },

  // color by element type

  // Représentant Légal
  {
    selector: `node[type="element"][name=${elementCodes.LegalRepresentative}]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return green60;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(green60);
      },
    },
  },
  {
    selector: `edge[type="edge-elem"][element=${elementCodes.LegalRepresentative}]`,
    style: {
      "line-color": green60,
      "target-arrow-color": green60,
    },
  },
  {
    selector: `node[type="identifier"][element=${elementCodes.LegalRepresentative}]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return green60;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(green60);
      },
    },
  },
  // Expert comptable
  {
    selector: `node[type="element"][code="${elementCodes.Accountant}"]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return magenta50;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(magenta50);
      },
    },
  },
  {
    selector: `edge[type="edge-elem"][code="${elementCodes.Accountant}"]`,
    style: {
      "line-color": magenta50,
      "target-arrow-color": magenta50,
    },
  },
  {
    selector: `node[type="identifier"][element="${elementCodes.Accountant}"]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return magenta50;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(magenta50);
      },
    },
  },
  // Entreprise
  {
    selector: `node[type="element"][code="${elementCodes.Company}"]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return purple60;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(purple60);
      },
    },
  },
  {
    selector: `edge[type="edge-elem"][element="${elementCodes.Company}"]`,
    style: {
      "line-color": purple60,
      "target-arrow-color": purple60,
    },
  },
  {
    selector: `node[type="identifier"][element="${elementCodes.Company}"]`,
    style: {
      "background-color": (elem) => {
        const getIcon = elem.data("icon");
        if (getIcon && getIcon()) {
          return colors.background.index;
        }
        return purple60;
      },
      "background-image": (elem) => {
        const getIcon = elem.data("icon");
        return getIcon(purple60);
      },
    },
  },
];
